import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useGenerateFrameImageMutation, useRegenerateFrameMutation } from '../queries/storyboard.ai.queries'
import { useStoryboardQuery, useSaveStoryboardMutation } from '../queries/storyboard.queries'
import { useStoryboardStore } from '../store/useStoryboardStore'
import { useRemoteConfigStore } from '@/features/shared/stores/config.store'
import {
    StoryboardFrame as StoryboardFrameType,
    StoryboardFrameRegenerationOptions,
    StoryboardImageGenerationOptions,
} from '../types/storyboard.types'
import AddStoryboardFrameDialog from './AddStoryboardFrameDialog'
import { ShotListPreviewPanel } from './ShotListPreviewPanel'
import StoryboardEmptySceneState from './StoryboardEmptySceneState'
import StoryboardFrame from './StoryboardFrame'
import StoryboardGenerationDialog from './StoryboardGenerationDialog'
import StoryboardHeader from './StoryboardHeader'
import StoryboardImageLightbox from './StoryboardImageLightbox'
import StoryboardNoFramesState from './StoryboardNoFramesState'

const normalizeFrameOrder = (inputFrames: StoryboardFrameType[]): StoryboardFrameType[] =>
    inputFrames.map((frame, index) => ({
        ...frame,
        frameNumber: index + 1,
    }))

const StoryboardMainContent = () => {
    const { project } = useWorkspace()
    const { editor } = useSceneStore()
    const {
        selectedSceneNode,
        selectedShot,
        selectShot
    } = useStoryboardStore()

    // Queries & Mutations
    const { data: frames = [], isLoading: isLoadingFrames } = useStoryboardQuery(project?.id || '', selectedSceneNode?.id ?? null)
    const { mutateAsync: generateFrameImage } = useGenerateFrameImageMutation()
    const { mutateAsync: regenerateFrame } = useRegenerateFrameMutation()
    const { mutateAsync: saveStoryboard } = useSaveStoryboardMutation(project?.id || '', selectedSceneNode?.id ?? '')

    const enableStoryboardAIImageGeneration = useRemoteConfigStore((state) => state.enableStoryboardAIImageGeneration)

    // Local state for frame regeneration tracking
    const [regeneratingFrameIds, setRegeneratingFrameIds] = useState<Set<string>>(new Set())
    const [regeneratingFrameAIIds, setRegeneratingFrameAIIds] = useState<Set<string>>(new Set())
    // Cache-busted URLs: after regeneration, store url with t=<timestamp> to force browser re-fetch
    const [frameCacheBusters, setFrameCacheBusters] = useState<Map<string, string>>(new Map())
    const bustUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`

    // Shot List Store integration
    const { loadShotList, shotLists } = useShotListStore()
    const currentSceneShots = selectedSceneNode ? (shotLists[selectedSceneNode.id] || []) : []

    const [showShotList, setShowShotList] = useState(false)
    const [isGenerationDialogOpen, setIsGenerationDialogOpen] = useState(false)
    const [isAddFrameDialogOpen, setIsAddFrameDialogOpen] = useState(false)
    const [generatingShot, setGeneratingShot] = useState<any | null>(null)
    const [uploadPreview, setUploadPreview] = useState<Record<string, string>>({})

    // Load shot list and storyboard when scene selection changes
    useEffect(() => {
        if (selectedSceneNode && project) {
            loadShotList(selectedSceneNode.id)
            selectShot(null) // Reset selected shot when scene changes
        }
    }, [selectedSceneNode, project, loadShotList, selectShot])

    // Generate local preview URLs for File/Blob image payloads
    useEffect(() => {
        const newPreviews: Record<string, string> = {}
        const objectUrls: string[] = []

        frames.forEach(frame => {
            const img = frame.imageUrl as any
            if (img && (img instanceof Blob || img instanceof File)) {
                const url = URL.createObjectURL(img)
                newPreviews[frame.id] = url
                objectUrls.push(url)
            }
        })

        if (Object.keys(newPreviews).length > 0) {
            setUploadPreview(prev => ({ ...prev, ...newPreviews }))
        }

        return () => {
            objectUrls.forEach(url => URL.revokeObjectURL(url))
        }
    }, [frames])

    const sceneTitle = editor?.read(() => {
        return selectedSceneNode ? getSceneHeader(selectedSceneNode) : "Select a Scene"
    })

    // Filter frames if a specific shot is selected
    const selectedShotFrames = selectedShot ? frames.filter(f => f.shotId === selectedShot.id) : []
    const hasSelectedShotFrames = selectedShotFrames.length > 0

    // Determine what to display
    const displayedFrames = selectedShot ? selectedShotFrames : frames

    const [viewingImage, setViewingImage] = useState<string | null>(null)

    const handleRegenerateFrame = async (frame: any) => {
        if (!selectedSceneNode || !frame.id) return

        setRegeneratingFrameIds(prev => new Set(prev).add(frame.id))

        try {
            console.log(`Regenerating frame ${frame.frameNumber} image`)

            const shot = currentSceneShots.find((item) => item.id === frame.shotId)
            if (!shot) throw new Error('Shot context not found for frame image generation')

            const imageOptions: StoryboardImageGenerationOptions = {
                frame,
                shot,
                referenceImageUrl: frame.referenceImageUrl,
                referenceImageUsage: frame.referenceImageUsage,
            }

            const { imageUrl } = await generateFrameImage(imageOptions)

            if (imageUrl) {
                const updatedFrames = frames.map(f => f.id === frame.id ? { ...f, imageUrl } : f)
                await saveStoryboard(updatedFrames)
                // Force browser to bypass its cache for this specific frame
                setFrameCacheBusters(prev => new Map(prev).set(frame.id, bustUrl(imageUrl)))
            }
        } catch (error) {
            console.error(`Error regenerating frame ${frame.id}:`, error)
        } finally {
            setRegeneratingFrameIds(prev => {
                const next = new Set(prev)
                next.delete(frame.id)
                return next
            })
        }
    }

    const handleRegenerateFrameAI = async (frame: any) => {
        if (!selectedSceneNode) return

        setRegeneratingFrameAIIds(prev => new Set(prev).add(frame.id))

        try {
            const shot = currentSceneShots.find((item) => item.id === frame.shotId)
            if (!shot) throw new Error('Shot context not found for frame regeneration')

            const sceneContext = editor?.read(
                () => selectedSceneNode.getCurrentSceneContent()
            ) || []
            const frameIndex = frames.findIndex((item) => item.id === frame.id)
            const regenerationOptions: StoryboardFrameRegenerationOptions = {
                frame,
                shot,
                sceneContext,
                frameContext: {
                    previousFrames: frameIndex > 0 ? frames.slice(Math.max(0, frameIndex - 2), frameIndex) : [],
                    nextFrames:
                        frameIndex >= 0
                            ? frames.slice(frameIndex + 1, Math.min(frames.length, frameIndex + 3))
                            : [],
                },
            }

            const updatedFrame = await regenerateFrame(regenerationOptions)

            const updatedFrames = frames.map(f => f.id === frame.id ? { ...f, ...updatedFrame } : f)
            await saveStoryboard(updatedFrames)
            // Force browser to bypass its cache for this specific frame
            if (updatedFrame.imageUrl) {
                setFrameCacheBusters(prev => new Map(prev).set(frame.id, bustUrl(updatedFrame.imageUrl!)))
            }
        } catch (error) {
            console.error(`Error regenerating frame ${frame.id}:`, error)
        } finally {
            setRegeneratingFrameAIIds(prev => {
                const next = new Set(prev)
                next.delete(frame.id)
                return next
            })
        }
    }


    const handleUpdateFrame = async (frameId: string, updates: any) => {
        if (!selectedSceneNode) return
        const updatedFrames = frames.map(f => f.id === frameId ? { ...f, ...updates } : f)
        await saveStoryboard(updatedFrames)
    }

    const handleRemoveFrame = async (frameId: string) => {
        if (!selectedSceneNode) return
        const updatedFrames = normalizeFrameOrder(frames.filter(f => f.id !== frameId))
        await saveStoryboard(updatedFrames)
    }

    const handleUploadFrameImage = async (frameId: string, file: File) => {
        if (!selectedSceneNode) return
        
        const previewUrl = URL.createObjectURL(file)
        setUploadPreview(prev => ({ ...prev, [frameId]: previewUrl }))
        
        const updatedFrames = frames.map(f => f.id === frameId ? { ...f, imageUrl: file as any } : f)
        await saveStoryboard(updatedFrames)
    }

    const initiateFrameGeneration = (shot: any) => {
        setGeneratingShot(shot)
        setIsGenerationDialogOpen(true)
    }

    const [recentlyCreatedFrameId, setRecentlyCreatedFrameId] = useState<string | null>(null)

    const openAddFrameDialog = () => {
        if (!selectedSceneNode || !project || currentSceneShots.length === 0) return
        setIsAddFrameDialogOpen(true)
    }

    const handleAddFrame = async (
        frameData: Omit<
            StoryboardFrameType,
            'id' | 'projectId' | 'sceneId' | 'frameNumber' | 'createdAt' | 'updatedAt'
        >
    ) => {
        if (!selectedSceneNode || !project) return

        const newFrameId = crypto.randomUUID()
        const nextFrameNumber = frames.length + 1

        const newFrame = {
            id: newFrameId,
            projectId: project.id,
            sceneId: selectedSceneNode.id,
            frameNumber: nextFrameNumber,
            createdAt: new Date(),
            updatedAt: new Date(),
            ...frameData,
        }

        const updatedFrames = normalizeFrameOrder([...frames, newFrame])

        // Optimistically set it to editing state
        setRecentlyCreatedFrameId(newFrameId)

        await saveStoryboard(updatedFrames)
    }

    const handleMoveFrame = async (frameId: string, direction: 'up' | 'down') => {
        if (!selectedSceneNode) return

        const visibleFrameIds = displayedFrames.map((frame) => frame.id)
        const currentVisibleIndex = visibleFrameIds.indexOf(frameId)
        if (currentVisibleIndex === -1) return

        const targetVisibleIndex =
            direction === 'up' ? currentVisibleIndex - 1 : currentVisibleIndex + 1
        if (targetVisibleIndex < 0 || targetVisibleIndex >= visibleFrameIds.length) return

        const targetFrameId = visibleFrameIds[targetVisibleIndex]
        const currentIndex = frames.findIndex((frame) => frame.id === frameId)
        const targetIndex = frames.findIndex((frame) => frame.id === targetFrameId)
        if (currentIndex === -1 || targetIndex === -1) return

        const updatedFrames = [...frames]
        const [movedFrame] = updatedFrames.splice(currentIndex, 1)
        updatedFrames.splice(targetIndex, 0, movedFrame)

        await saveStoryboard(normalizeFrameOrder(updatedFrames))
    }

    if (!selectedSceneNode) {
        return <StoryboardEmptySceneState />
    }

    return (
        <div className="flex flex-1 w-full h-full overflow-hidden bg-background min-w-0">
            <div className="flex w-full h-full overflow-hidden min-w-0">
                <div className={cn("flex flex-col h-full overflow-hidden transition-all duration-300 min-w-0", showShotList ? "w-2/3 lg:w-3/4" : "w-full")}>
                    <StoryboardHeader
                        sceneTitle={sceneTitle || "Select a Scene"}
                        displayedFramesCount={displayedFrames.length}
                        isFiltered={!!selectedShot}
                        selectedShot={selectedShot}
                        hasSelectedShotFrames={hasSelectedShotFrames}
                        hasFrames={frames.length > 0}
                        currentSceneShots={currentSceneShots}
                        showShotList={showShotList}
                        onInitiateFrameGeneration={initiateFrameGeneration}
                        onCreateManualFrame={openAddFrameDialog}
                        onClearSelection={() => selectShot(null)}
                        onRegenerateAll={() => setIsGenerationDialogOpen(true)}
                        onToggleShotList={() => setShowShotList(true)}
                        onAddFrame={openAddFrameDialog}
                    />

                    <div className="flex-1 overflow-hidden h-full">
                        <ScrollArea className="h-full w-full">
                            <div className="p-6 max-w-5xl mx-auto">
                                {isLoadingFrames ? (
                                    <div className="flex flex-col items-center justify-center h-[50vh]">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                        <p className="mt-4 text-muted-foreground">Loading storyboard...</p>
                                    </div>
                                ) : displayedFrames.length === 0 ? (
                                    <StoryboardNoFramesState
                                        selectedShot={selectedShot}
                                        onToggleShotList={() => setShowShotList(true)}
                                        onInitiateFrameGeneration={initiateFrameGeneration}
                                        onCreateManually={openAddFrameDialog}
                                    />
                                ) : (
                                    <div className="flex flex-col gap-6 transition-all duration-300 pb-20">
                                        {displayedFrames.map((frame, index) => {
                                            const frameShot =
                                                currentSceneShots.find((shot) => shot.id === frame.shotId) ??
                                                null

                                            return (
                                            <div
                                                id={frame.id}
                                                key={frame.id}
                                                className="transition-all duration-30"
                                            >
                                                <StoryboardFrame
                                                    frame={frame}
                                                    shot={frameShot}
                                                    displayImageUrl={uploadPreview[frame.id] ?? frameCacheBusters.get(frame.id) ?? (typeof frame.imageUrl === 'string' ? frame.imageUrl : undefined)}
                                                    onViewImage={(url) => setViewingImage(url)}
                                                    onRegenerateImage={enableStoryboardAIImageGeneration ? () => handleRegenerateFrame(frame) : undefined}
                                                    isRegeneratingImage={regeneratingFrameIds.has(frame.id)}
                                                    onUploadImage={(file) => handleUploadFrameImage(frame.id, file)}
                                                    onRegenerateFrame={() => handleRegenerateFrameAI(frame)}
                                                    isRegeneratingFrame={regeneratingFrameAIIds.has(frame.id)}
                                                    onUpdate={handleUpdateFrame}
                                                    onRemove={handleRemoveFrame}
                                                    onMoveUp={() => handleMoveFrame(frame.id, 'up')}
                                                    onMoveDown={() => handleMoveFrame(frame.id, 'down')}
                                                    isFirstFrame={index === 0}
                                                    isLastFrame={index === displayedFrames.length - 1}
                                                    defaultEditing={recentlyCreatedFrameId === frame.id}
                                                />
                                            </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </div>

                {showShotList && (
                    <div className="w-1/3 lg:w-1/4 border-l flex flex-col h-full bg-background transition-all duration-300">
                        {selectedSceneNode ? (
                            <ShotListPreviewPanel
                                sceneId={selectedSceneNode.id}
                                onClose={() => setShowShotList(false)}
                                onShotClick={(shot) => selectShot(shot)}
                                selectedShotId={selectedShot?.id}
                            />
                        ) : (
                            <div className="p-4">Select a scene</div>
                        )}
                    </div>
                )}
            </div>

            <StoryboardGenerationDialog
                open={isGenerationDialogOpen}
                onOpenChange={(open) => {
                    setIsGenerationDialogOpen(open)
                    if (!open) setGeneratingShot(null)
                }}
                shot={generatingShot}
            />

            <AddStoryboardFrameDialog
                open={isAddFrameDialogOpen}
                onOpenChange={setIsAddFrameDialogOpen}
                currentSceneShots={currentSceneShots}
                selectedShot={selectedShot}
                onAddFrame={handleAddFrame}
            />

            {viewingImage && (
                <StoryboardImageLightbox
                    imageUrl={viewingImage}
                    onClose={() => setViewingImage(null)}
                />
            )}
        </div>
    )
}

export default StoryboardMainContent
